// src/pages/VehicleV2/VehicleSaleAndScrapDoc.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FaArrowLeft, FaFolderOpen } from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/vehicleSalesorScrapReport";

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

export default function VehicleSaleAndScrapDoc() {
  const { skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ✅ saleScrapId from query (?saleorscrapId=123) OR route state
  const saleScrapId = useMemo(() => {
    const q = toInt(searchParams.get("saleorscrapId"));
    const s = toInt(location?.state?.saleScrapId);
    return q || s || 0;
  }, [searchParams, location?.state]);

  const [loading, setLoading] = useState(false);
  const [docs, setDocs] = useState([]);
  const [message, setMessage] = useState("");

  const loadDocs = async () => {
    if (!saleScrapId) {
      setDocs([]);
      setMessage("Invalid sale/scrap id");
      return;
    }

    setLoading(true);
    try {
      // ✅ No rights params anymore
      const { data } = await axios.get(`${API_BASE}/doc`, {
        params: { saleScrapId },
      });

      if (!data?.success) {
        setDocs([]);
        setMessage(data?.message || "Failed to load documents");
        toast.error(data?.message || "Failed to load documents");
        return;
      }

      if (!data.docs?.length) {
        setDocs([]);
        setMessage(data.message || "There is no Documents for this Vehicle..");
        toast.info(data.message || "There is no Documents for this Vehicle..");
      } else {
        setDocs(data.docs);
        setMessage("");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load documents";
      setDocs([]);
      setMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleScrapId]);

  const openDoc = (doc) => {
    if (!doc?.url) return;
    window.open(doc.url, "_blank", "noopener,noreferrer");
  };

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  return (
    <>
      <HeaderBar />
      <VehicleLayoutWrapper>
        <ToastContainer />

        <div className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="text-lg font-semibold uppercase">
              Vehicle Sale and Scrap Document
            </div>

            <div className="text-sm text-gray-600">
              INFRA SCREEN : B-27/2
            </div>
          </div>

          {/* Back button */}
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className={`${buttonClass} ${hoverClass} text-white px-3 py-2 rounded flex items-center gap-2`}
            >
              <FaArrowLeft />
              Back
            </button>

            {saleScrapId ? (
              <div className="text-sm text-gray-600">
                Sale/Scrap ID: <b>{saleScrapId}</b>
              </div>
            ) : null}
          </div>

          {/* Body */}
          <div className="mt-4 border rounded bg-white p-4">
            <div className="font-medium mb-2">Uploaded Documents:</div>

            {loading ? (
              <div className="text-sm text-gray-600">Loading...</div>
            ) : message ? (
              <div className="text-sm text-gray-700">{message}</div>
            ) : (
              <div className="space-y-2">
                <div className="border rounded p-2 max-w-xl">
                  {docs.map((d, idx) => (
                    <button
                      key={`${d.fileName}-${idx}`}
                      type="button"
                      onClick={() => openDoc(d)}
                      className="w-full text-left px-3 py-2 rounded hover:bg-gray-100 flex items-center gap-2"
                      title="Open document"
                    >
                      <FaFolderOpen className="text-gray-600" />
                      <span className="truncate">{d.fileName}</span>
                    </button>
                  ))}
                </div>

                <div className="text-xs text-gray-500">
                  Click a document to open.
                </div>
              </div>
            )}
          </div>
        </div>
      </VehicleLayoutWrapper>
    </>
  );
}
